import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/common/PageHeader";
import { BudgetSummary } from "@/components/common/BudgetSummary";
import { PriorityBadge, StatusBadge } from "@/components/common/StatusBadge";
import { PartialItemsEditor, partialChanges, partialTotal } from "@/components/approvals/PartialItemsEditor";
import { AiApprovalSummary } from "@/components/approvals/AiApprovalSummary";
import { useAiSettings } from "@/hooks/useAiSettings";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { SERVICE_META, fmtDate, fmtDateTime, fmtRp } from "@/lib/constants";
import { cn } from "@/lib/utils";

const URGENT_BORDER = { critical: "border-rose-500/70 shadow-[0_0_0_1px_rgba(244,63,94,.3)]", high: "border-amber-500/60" };

function ApprovalCard({ title, meta, code, link, onDecide, testId, items, priority, ticketId, aiInitial }) {
  const [note, setNote] = useState("");
  const [changes, setChanges] = useState({});
  const hasItems = Array.isArray(items) && items.length > 0;
  const partial = hasItems ? partialChanges(items, changes) : [];
  const applyAi = (adj) => setChanges(Object.fromEntries(adj.map((a) => [a.index, a.rejected ? { rejected: true, note: a.reason } : { approved_qty: Number(a.approved_qty), note: a.reason }])));
  const approve = () => {
    if (hasItems && partial.length && items.every((_, i) => changes[i]?.rejected)) return toast.error("Semua item ditolak — gunakan tombol Reject");
    onDecide("approve", note, partial.length ? partial : undefined);
  };
  return (
    <div className={cn("op-card space-y-3 p-4", URGENT_BORDER[priority])} data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div><div className="font-mono text-xs text-muted-foreground">{code}</div><div className="font-semibold">{title}</div><div className="text-xs text-muted-foreground">{meta}</div></div>
        {link && <Link to={link} className="text-xs text-primary hover:underline">Detail</Link>}
      </div>
      {ticketId && <AiApprovalSummary ticketId={ticketId} initial={aiInitial} onApplyItems={hasItems ? applyAi : undefined} testId={testId} />}
      {hasItems && <PartialItemsEditor items={items} changes={changes} onChange={setChanges} testId={`${testId}-items`} />}
      <Textarea placeholder="Catatan (opsional, wajib untuk reject/revise)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid={`${testId}-note`} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={approve} data-testid={`${testId}-approve`}>{partial.length ? `Approve Parsial (${fmtRp(partialTotal(items, changes))})` : "Approve"}</Button>
        <Button size="sm" variant="outline" onClick={() => (note.trim() ? onDecide("revise", note) : toast.error("Catatan wajib untuk revise"))} data-testid={`${testId}-revise`}>Revise</Button>
        <Button size="sm" variant="destructive" onClick={() => (note.trim() ? onDecide("reject", note) : toast.error("Catatan wajib untuk reject"))} data-testid={`${testId}-reject`}>Reject</Button>
      </div>
    </div>
  );
}

export default function Approvals() {
  const { data, refetch } = useFetch("/approvals");
  const { on: aiOn } = useAiSettings();
  const decide = (url) => async (decision, note, items) => {
    try { await api.post(url, { decision, note, items }); toast.success(items ? "Approve parsial tersimpan — jumlah & biaya diperbarui" : `Keputusan: ${decision}`); refetch(); } catch (e) { toast.error(errMsg(e)); }
  };
  const tickets = data?.tickets || []; const docs = data?.documents || [];
  return (
    <div className="space-y-6 fade-up">
      <PageHeader title="Approval Inbox" subtitle="Request dan dokumen yang menunggu keputusan Anda — diurutkan berdasarkan urgensi (Critical → High → Low, lalu waktu dibutuhkan). Approval biaya bertahap: Supervisor (check) → Manager → COO → CEO." />
      <BudgetSummary refreshKey={tickets.length} />
      <section>
        <h2 className="mb-3 text-base font-semibold md:text-lg">Service Request <span className="font-mono text-sm text-muted-foreground">({tickets.length})</span></h2>
        {tickets.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-ticket-approvals">Tidak ada request menunggu approval.</p>}
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="approval-ticket-list">
          {tickets.map((t) => (
            <ApprovalCard key={t.id} code={t.code} priority={t.priority} items={t.details?.items} ticketId={aiOn("approval_summary") ? t.id : null} aiInitial={t.ai_summary && t.ai_summary.summary ? { ...t.ai_summary, cached: true } : null}
              title={<span className="flex flex-wrap items-center gap-2">{t.title}<PriorityBadge priority={t.priority} /><span className="font-mono text-xs" data-testid={`approval-cost-${t.code}`}>{fmtRp(t.cost ?? 0)}</span>{t.needed_at && <span className="flex items-center gap-1 text-[11px] text-amber-600"><Clock className="h-3 w-3" />{fmtDateTime(t.needed_at)}</span>}</span>}
              meta={`${SERVICE_META[t.service_type]?.title} · ${t.requester_name} · ${fmtDateTime(t.created_at)}${t.approval_progress ? " · " + t.approval_progress : ""}`} link={`/tickets/${t.id}`} onDecide={decide(`/tickets/${t.id}/approve`)} testId={`approval-ticket-${t.code}`} />
          ))}
        </div>
      </section>
      <section>
        <h2 className="mb-3 text-base font-semibold md:text-lg">Dokumen Legal <span className="font-mono text-sm text-muted-foreground">({docs.length})</span></h2>
        {docs.length === 0 && <p className="text-sm text-muted-foreground" data-testid="no-doc-approvals">Tidak ada dokumen menunggu approval.</p>}
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {docs.map((d) => (
            <ApprovalCard key={d.id} code={d.code} title={<span className="flex items-center gap-2">{d.title}<StatusBadge status={d.expiry_state} /></span>}
              meta={`${d.doc_number || "-"} · ${d.issuer || "-"} · exp ${fmtDate(d.expiry_date)} · v${d.current_version}`} link="/documents" onDecide={decide(`/documents/${d.id}/approve`)} testId={`approval-doc-${d.code}`} />
          ))}
        </div>
      </section>
    </div>
  );
}
